import React, { useState, useEffect } from 'react';
import { Button, Col, Form, Modal, Row, Spinner, Card, Badge, Alert } from 'react-bootstrap';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, getBearerToken } from '../../functions';

const UmrahCustomPricing = ({ show, setShow, selectedBooking, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [customPrices, setCustomPrices] = useState({
    custom_adult_price: '',
    custom_child_price: '',
    custom_infant_price: ''
  });

  // Get original prices from package for the booking's room type
  const getOriginalPrices = () => {
    if (!selectedBooking?.umrah_package?.prices) return null;
    
    const roomType = selectedBooking.room_type;
    const originalPricing = selectedBooking.umrah_package.prices.find(p => p.room_type === roomType);
    
    return originalPricing || null;
  };

  const originalPricing = getOriginalPrices();

  // Initialize custom prices when modal opens
  useEffect(() => {
    if (show && selectedBooking) {
      if (selectedBooking.is_custom_pricing) {
        // Use existing custom prices (only for passenger types present in booking)
        setCustomPrices({
          custom_adult_price: selectedBooking.number_of_adults > 0 ? (selectedBooking.custom_adult_price || '') : '',
          custom_child_price: selectedBooking.number_of_children > 0 ? (selectedBooking.custom_child_price || '') : '',
          custom_infant_price: selectedBooking.number_of_infants > 0 ? (selectedBooking.custom_infant_price || '') : ''
        });
      } else {
        // Use original package prices (only for passenger types present in booking)
        setCustomPrices({
          custom_adult_price: selectedBooking.number_of_adults > 0 ? (originalPricing?.price_adult || '') : '',
          custom_child_price: selectedBooking.number_of_children > 0 ? (originalPricing?.price_child || '') : '',
          custom_infant_price: selectedBooking.number_of_infants > 0 ? (originalPricing?.price_infant || '') : ''
        });
      }
    }
  }, [show, selectedBooking, originalPricing]);

  const handleClose = () => {
    setShow(false);
    setCustomPrices({
      custom_adult_price: '',
      custom_child_price: '',
      custom_infant_price: ''
    });
  };

  const updateCustomPrice = (field, value) => {
    setCustomPrices(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate new total amount
  const calculateNewTotal = () => {
    const adultTotal = (parseFloat(customPrices.custom_adult_price) || 0) * (selectedBooking?.number_of_adults || 0);
    const childTotal = (parseFloat(customPrices.custom_child_price) || 0) * (selectedBooking?.number_of_children || 0);
    const infantTotal = (parseFloat(customPrices.custom_infant_price) || 0) * (selectedBooking?.number_of_infants || 0);
    
    return adultTotal + childTotal + infantTotal;
  };

  // Calculate package discount
  const calculatePackageDiscount = () => {
    if (!selectedBooking?.umrah_package) return { total: 0, adult: 0, child: 0, infant: 0 };
    
    const umrahPackage = selectedBooking.umrah_package;
    const adultPrice = parseFloat(customPrices.custom_adult_price) || 0;
    const childPrice = parseFloat(customPrices.custom_child_price) || 0;
    const infantPrice = parseFloat(customPrices.custom_infant_price) || 0;
    
    let adultDiscount = 0;
    let childDiscount = 0;
    let infantDiscount = 0;
    
    if (umrahPackage.discount_type === 'percentage') {
      if (umrahPackage.discount_adult) {
        adultDiscount = (adultPrice * umrahPackage.discount_adult / 100) * (selectedBooking?.number_of_adults || 0);
      }
      if (umrahPackage.discount_child) {
        childDiscount = (childPrice * umrahPackage.discount_child / 100) * (selectedBooking?.number_of_children || 0);
      }
      if (umrahPackage.discount_infant) {
        infantDiscount = (infantPrice * umrahPackage.discount_infant / 100) * (selectedBooking?.number_of_infants || 0);
      }
    } else if (umrahPackage.discount_type === 'fixed') {
      if (umrahPackage.discount_adult) {
        adultDiscount = Math.min(umrahPackage.discount_adult, adultPrice) * (selectedBooking?.number_of_adults || 0);
      }
      if (umrahPackage.discount_child) {
        childDiscount = Math.min(umrahPackage.discount_child, childPrice) * (selectedBooking?.number_of_children || 0);
      }
      if (umrahPackage.discount_infant) {
        infantDiscount = Math.min(umrahPackage.discount_infant, infantPrice) * (selectedBooking?.number_of_infants || 0);
      }
    }
    
    return {
      total: adultDiscount + childDiscount + infantDiscount,
      adult: adultDiscount,
      child: childDiscount,
      infant: infantDiscount
    };
  };

  // Calculate final amount
  const calculateFinalAmount = () => {
    const newTotal = calculateNewTotal();
    const packageDiscount = calculatePackageDiscount().total;
    const additionalDiscount = selectedBooking?.additional_discount_amount || 0;
    
    return newTotal - packageDiscount - additionalDiscount;
  };

  const handleSubmit = async () => {
    // Validate only the prices for passenger types that are present in the booking
    const validationErrors = [];
    
    if (selectedBooking.number_of_adults > 0 && !customPrices.custom_adult_price) {
      validationErrors.push('Adult price is required');
    }
    if (selectedBooking.number_of_children > 0 && !customPrices.custom_child_price) {
      validationErrors.push('Child price is required');
    }
    if (selectedBooking.number_of_infants > 0 && !customPrices.custom_infant_price) {
      validationErrors.push('Infant price is required');
    }
    
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    const tid = toast.loading('Updating custom pricing...');

    try {
      // Only send prices for passenger types that are present in the booking
      const payload = {};
      if (selectedBooking.number_of_adults > 0) {
        payload.custom_adult_price = customPrices.custom_adult_price;
      }
      if (selectedBooking.number_of_children > 0) {
        payload.custom_child_price = customPrices.custom_child_price;
      }
      if (selectedBooking.number_of_infants > 0) {
        payload.custom_infant_price = customPrices.custom_infant_price;
      }

      const response = await axios.post(
        `${API_BASE_URL}/umrah-bookings/${selectedBooking.id}/update-custom-pricing`,
        payload,
        {
          headers: { Authorization: `Bearer ${getBearerToken()}` }
        }
      );

      if (response.data.status) {
        toast.success('Custom pricing updated successfully!');
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.data.message || 'Failed to update custom pricing');
      }
    } catch (error) {
      console.error('Error updating custom pricing:', error);
      toast.error(error.response?.data?.message || 'Failed to update custom pricing');
    } finally {
      setLoading(false);
      toast.dismiss(tid);
    }
  };

  if (!selectedBooking || !originalPricing) {
    return null;
  }

  return (
    <Modal show={show} size="lg" onHide={handleClose} centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton className="border-0 pb-2">
        <Modal.Title className="d-flex align-items-center">
          <i className="fas fa-edit me-2 text-primary"></i>
          Edit Custom Pricing - {selectedBooking.umrah_package?.title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 py-2">
        {/* Booking Info */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <h6 className="mb-3">Booking Details</h6>
                <p><strong>Room Type:</strong> <Badge bg="info" className="text-capitalize">{selectedBooking.room_type}</Badge></p>
                <p><strong>Passengers:</strong> {selectedBooking.number_of_adults} Adults, {selectedBooking.number_of_children} Children, {selectedBooking.number_of_infants} Infants</p>
                <p><strong>Current Status:</strong> 
                  {selectedBooking.is_custom_pricing ? (
                    <Badge bg="warning" className="ms-2">Custom Pricing Applied</Badge>
                  ) : (
                    <Badge bg="secondary" className="ms-2">Original Package Pricing</Badge>
                  )}
                </p>
              </Col>
              <Col md={6}>
                <h6 className="mb-3">Original Package Prices</h6>
                <div className="bg-light p-3 rounded">
                  <p><strong>Adult:</strong> PKR {originalPricing.price_adult?.toLocaleString()}</p>
                  <p><strong>Child:</strong> PKR {originalPricing.price_child?.toLocaleString()}</p>
                  <p><strong>Infant:</strong> PKR {originalPricing.price_infant?.toLocaleString()}</p>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Custom Pricing Form */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Set Custom Prices</h6>
            <small className="text-muted">Only pricing fields for passengers in this booking are shown</small>
          </Card.Header>
          <Card.Body>
            <Row>
              {/* Adult Price - Only show if there are adults */}
              {selectedBooking.number_of_adults > 0 && (
                <Col md={selectedBooking.number_of_children > 0 || selectedBooking.number_of_infants > 0 ? 4 : 12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Custom Adult Price (PKR)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={customPrices.custom_adult_price}
                      onChange={(e) => updateCustomPrice('custom_adult_price', e.target.value)}
                      placeholder="Enter custom adult price"
                    />
                    <Form.Text className="text-muted">
                      Original: PKR {originalPricing.price_adult?.toLocaleString()} • {selectedBooking.number_of_adults} Adult{selectedBooking.number_of_adults > 1 ? 's' : ''}
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}

              {/* Child Price - Only show if there are children */}
              {selectedBooking.number_of_children > 0 && (
                <Col md={selectedBooking.number_of_adults > 0 || selectedBooking.number_of_infants > 0 ? 4 : 12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Custom Child Price (PKR)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={customPrices.custom_child_price}
                      onChange={(e) => updateCustomPrice('custom_child_price', e.target.value)}
                      placeholder="Enter custom child price"
                    />
                    <Form.Text className="text-muted">
                      Original: PKR {originalPricing.price_child?.toLocaleString()} • {selectedBooking.number_of_children} Child{selectedBooking.number_of_children > 1 ? 'ren' : ''}
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}

              {/* Infant Price - Only show if there are infants */}
              {selectedBooking.number_of_infants > 0 && (
                <Col md={selectedBooking.number_of_adults > 0 || selectedBooking.number_of_children > 0 ? 4 : 12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Custom Infant Price (PKR)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={customPrices.custom_infant_price}
                      onChange={(e) => updateCustomPrice('custom_infant_price', e.target.value)}
                      placeholder="Enter custom infant price"
                    />
                    <Form.Text className="text-muted">
                      Original: PKR {originalPricing.price_infant?.toLocaleString()} • {selectedBooking.number_of_infants} Infant{selectedBooking.number_of_infants > 1 ? 's' : ''}
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>

        {/* Real-time Calculation */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Price Calculation Preview</h6>
          </Card.Header>
          <Card.Body>
            <div className="pricing-breakdown">
              {/* Adult Calculation */}
              {selectedBooking.number_of_adults > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-user me-2 text-primary"></i>
                    <span>Adults ({selectedBooking.number_of_adults} × PKR {(parseFloat(customPrices.custom_adult_price) || 0).toLocaleString()})</span>
                  </div>
                  <div className="text-end">
                    <span className="fontweight600">PKR {((parseFloat(customPrices.custom_adult_price) || 0) * selectedBooking.number_of_adults).toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Child Calculation */}
              {selectedBooking.number_of_children > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-child me-2 text-warning"></i>
                    <span>Children ({selectedBooking.number_of_children} × PKR {(parseFloat(customPrices.custom_child_price) || 0).toLocaleString()})</span>
                  </div>
                  <div className="text-end">
                    <span className="fontweight600">PKR {((parseFloat(customPrices.custom_child_price) || 0) * selectedBooking.number_of_children).toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Infant Calculation */}
              {selectedBooking.number_of_infants > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-baby me-2 text-info"></i>
                    <span>Infants ({selectedBooking.number_of_infants} × PKR {(parseFloat(customPrices.custom_infant_price) || 0).toLocaleString()})</span>
                  </div>
                  <div className="text-end">
                    <span className="fontweight600">PKR {((parseFloat(customPrices.custom_infant_price) || 0) * selectedBooking.number_of_infants).toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Subtotal */}
              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <div className="d-flex align-items-center">
                  <i className="fas fa-plus-circle me-2 text-dark"></i>
                  <span className="fontweight600">Subtotal (Before Discount)</span>
                </div>
                <div className="text-end">
                  <span className="fontweight600">PKR {calculateNewTotal().toLocaleString()}</span>
                </div>
              </div>

              {/* Adult Discount */}
              {selectedBooking.number_of_adults > 0 && calculatePackageDiscount().adult > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-tag me-2 text-success"></i>
                    <span className="fontweight600 text-success">
                      Adult Discount ({selectedBooking.umrah_package?.discount_adult}{selectedBooking.umrah_package?.discount_type === 'percentage' ? '%' : ' PKR'} × {selectedBooking.number_of_adults})
                    </span>
                  </div>
                  <div className="text-end">
                    <span className="fontweight600 text-success">-PKR {calculatePackageDiscount().adult.toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Child Discount */}
              {selectedBooking.number_of_children > 0 && calculatePackageDiscount().child > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-tag me-2 text-success"></i>
                    <span className="fontweight600 text-success">
                      Child Discount ({selectedBooking.umrah_package?.discount_child}{selectedBooking.umrah_package?.discount_type === 'percentage' ? '%' : ' PKR'} × {selectedBooking.number_of_children})
                    </span>
                  </div>
                  <div className="text-end">
                    <span className="fontweight600 text-success">-PKR {calculatePackageDiscount().child.toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Infant Discount */}
              {selectedBooking.number_of_infants > 0 && calculatePackageDiscount().infant > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-tag me-2 text-success"></i>
                    <span className="fontweight600 text-success">
                      Infant Discount ({selectedBooking.umrah_package?.discount_infant}{selectedBooking.umrah_package?.discount_type === 'percentage' ? '%' : ' PKR'} × {selectedBooking.number_of_infants})
                    </span>
                  </div>
                  <div className="text-end">
                    <span className="fontweight600 text-success">-PKR {calculatePackageDiscount().infant.toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Total Package Discount */}
              {calculatePackageDiscount().total > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-tag me-2 text-success"></i>
                    <span className="fontweight600 text-success">
                      Total Package Discount
                    </span>
                  </div>
                  <div className="text-end">
                    <span className="fontweight600 text-success">-PKR {calculatePackageDiscount().total.toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Additional Discount */}
              {selectedBooking.additional_discount_amount > 0 && (
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-tag me-2 text-success"></i>
                    <span className="fontweight600 text-success">
                      Additional Discount
                      {selectedBooking.additional_discount_name && ` (${selectedBooking.additional_discount_name})`}
                    </span>
                  </div>
                  <div className="text-end">
                    <span className="fontweight600 text-success">-PKR {selectedBooking.additional_discount_amount.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Final Amount */}
            <div className="mt-3 p-3 bg-light rounded">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-success">Final Amount</h5>
                <h4 className="mb-0 text-success fontweight700">
                  PKR {calculateFinalAmount().toLocaleString()}
                </h4>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Warning Alert */}
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> This will override the original package pricing for this booking only. 
        </Alert>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <div className="d-flex justify-content-between align-items-center w-100">
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={loading || 
              (selectedBooking.number_of_adults > 0 && !customPrices.custom_adult_price) ||
              (selectedBooking.number_of_children > 0 && !customPrices.custom_child_price) ||
              (selectedBooking.number_of_infants > 0 && !customPrices.custom_infant_price)
            }
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                Update Custom Pricing
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default UmrahCustomPricing;
